/**
 * Harmonogramy w formularzach: typy, dni tygodnia, zakresy i daty referencyjne.
 */
import { addDays, differenceInCalendarDays, startOfWeek } from 'date-fns'

/** Rodzaj harmonogramu: stały, zakres czasowy jednorazowy lub wzorzec cykliczny. */
export enum AccessScheduleType {
  Constant = 'constant',
  Temporary = 'temporary',
  Schedule = 'schedule',
}

/** Częstotliwość wzorca: dziennie, co tydzień (spójny blok dni) lub wybrane dni (BYDAY). */
export enum Frequency {
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  SelectedDays = 'SELECTED_DAYS',
}

/** Kody dni w kolejności ISO (poniedziałek = MO) — zgodne z iCal BYDAY. */
export const WEEK_DAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']

/** Prefiksy kluczy i18n dla nazw dni (namespace resources/profiles). */
export const DAY_TRANSLATION_KEYS: Record<string, string> = {
  MO: 'accessSchedule.days.monday',
  TU: 'accessSchedule.days.tuesday',
  WE: 'accessSchedule.days.wednesday',
  TH: 'accessSchedule.days.thursday',
  FR: 'accessSchedule.days.friday',
  SA: 'accessSchedule.days.saturday',
  SU: 'accessSchedule.days.sunday',
}

/** Domyślna strefa IANA przy eksporcie iCal (brak jawnej strefy w formularzu). */
export const DEFAULT_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw'

function normalizeWeekdayCode(day: string): string {
  return String(day).toUpperCase()
}

function weekdayIndex(day: string): number {
  return WEEK_DAYS.indexOf(normalizeWeekdayCode(day))
}

/** Sortuje kody dni zgodnie z kolejnością tygodnia (poniedziałek → niedziela). */
export const sortWeekdays = (daysOfWeek: string[]) =>
  [...daysOfWeek].sort((dayA, dayB) => weekdayIndex(dayA) - weekdayIndex(dayB))

/** Ile dni kalendarzowych między „od” a „do” włącznie (z zawinięciem przez niedzielę). */
export function daysBetweenWeekdays(fromDay: string, toDay: string): number {
  const fromIndex = weekdayIndex(fromDay)
  const toIndex = weekdayIndex(toDay)
  if (fromIndex === -1 || toIndex === -1) return 0
  if (fromIndex <= toIndex) return toIndex - fromIndex
  return WEEK_DAYS.length - fromIndex + toIndex
}

/** Czy zakres „od–do” przechodzi przez koniec tygodnia (np. czwartek → środa). */
export function weekdayRangeWrapsAround(
  fromDay: string,
  toDay: string
): boolean {
  const fromIndex = weekdayIndex(fromDay)
  const toIndex = weekdayIndex(toDay)
  return fromIndex !== -1 && toIndex !== -1 && fromIndex > toIndex
}

/**
 * Końce jednego ciągłego zakresu dni (pn→nd lub z zawinięciem).
 * `from` / `to` odpowiadają wyborowi w selectach „Od” / „Do”.
 */
export function getWeekdayRangeEnds(
  daysOfWeek: string[]
): { from: string; to: string } | null {
  if (daysOfWeek.length === 0) return null

  const daysInOrder = Array.from(new Set(daysOfWeek)).map(normalizeWeekdayCode)
  const dayIndices = daysInOrder.map(day => weekdayIndex(day))
  if (dayIndices.some(index => index < 0)) return null

  let isContiguousOnWeekCircle = true
  for (let index = 1; index < dayIndices.length; index++) {
    const previousIndex = dayIndices[index - 1]
    const currentIndex = dayIndices[index]
    const step =
      (currentIndex - previousIndex + WEEK_DAYS.length) % WEEK_DAYS.length
    if (step !== 1) {
      isContiguousOnWeekCircle = false
      break
    }
  }

  if (isContiguousOnWeekCircle) {
    return {
      from: daysInOrder[0],
      to: daysInOrder[daysInOrder.length - 1],
    }
  }

  const sortedDays = sortWeekdays(daysInOrder)
  const sortedIndices = sortedDays.map(day => weekdayIndex(day))
  for (let index = 1; index < sortedIndices.length; index++) {
    if (sortedIndices[index] !== sortedIndices[index - 1] + 1) return null
  }
  return { from: sortedDays[0], to: sortedDays[sortedDays.length - 1] }
}

/** Lista kodów dni od `fromDay` do `toDay` włącznie (liniowo lub z zawinięciem tygodnia). */
export function expandWeekdayRange(fromDay: string, toDay: string): string[] {
  const fromCode = normalizeWeekdayCode(fromDay)
  const toCode = normalizeWeekdayCode(toDay)
  const fromIndex = weekdayIndex(fromCode)
  const toIndex = weekdayIndex(toCode)
  if (fromIndex === -1 || toIndex === -1) return []
  if (fromIndex <= toIndex) return [...WEEK_DAYS.slice(fromIndex, toIndex + 1)]
  return [...WEEK_DAYS.slice(fromIndex), ...WEEK_DAYS.slice(0, toIndex + 1)]
}

/** Kod dnia tygodnia (MO…SU) dla podanej daty w czasie lokalnym. */
export const weekdayCodeFromDate = (date: Date): string =>
  WEEK_DAYS[(date.getDay() + 6) % 7]

/** Północ wybranego dnia w bieżącym tygodniu kalendarzowym (tydzień od poniedziałku). */
export const dateForWeekdayThisWeek = (dayOfWeek: string) => {
  const dayIndex = weekdayIndex(dayOfWeek)
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })

  if (dayIndex === -1) return weekStart

  return addDays(weekStart, dayIndex)
}

/** Data z kalendarza + godzina z osobnego pola czasu. */
export const combineDateAndTime = (date: Date, time: Date | null) => {
  const result = new Date(date)

  if (!time) return result

  result.setHours(time.getHours(), time.getMinutes(), 0, 0)

  return result
}

/**
 * Gdy start nie wpada w wybrane dni — przesuwa początek na pierwszy dzień z listy,
 * zachowując długość przedziału czasu.
 */
export const alignRangeToFirstWeekday = (
  fromDate: Date,
  toDate: Date,
  daysOfWeek: string[]
): { fromDate: Date; toDate: Date } => {
  if (!daysOfWeek.length) return { fromDate, toDate }

  const selectedDays = new Set(daysOfWeek)
  const startWeekday = weekdayCodeFromDate(fromDate)
  if (selectedDays.has(startWeekday)) return { fromDate, toDate }

  const durationMs = toDate.getTime() - fromDate.getTime()
  if (durationMs <= 0) return { fromDate, toDate }

  const sortedDays = sortWeekdays(daysOfWeek)
  const rangeEnds = getWeekdayRangeEnds(daysOfWeek)
  const firstWeekday = rangeEnds?.from ?? sortedDays[0]
  const weekStart = startOfWeek(fromDate, { weekStartsOn: 1 })
  const firstDayDate = addDays(weekStart, weekdayIndex(firstWeekday))
  const newFrom = combineDateAndTime(firstDayDate, fromDate)
  const newTo = new Date(newFrom.getTime() + durationMs)
  return { fromDate: newFrom, toDate: newTo }
}

/** Daty referencyjne „od–do” pod wybraną częstotliwość i dni tygodnia. */
export const getReferenceScheduleDates = ({
  frequency,
  daysOfWeek,
}: {
  frequency: Frequency
  daysOfWeek: string[]
}) => {
  if (frequency === Frequency.Daily) {
    const today = new Date()
    return {
      startDate: today,
      endDate: today,
    }
  }

  const sortedDays = sortWeekdays(daysOfWeek)
  const rangeEnds =
    frequency === Frequency.Weekly ? getWeekdayRangeEnds(daysOfWeek) : null

  const fromDay = rangeEnds?.from ?? sortedDays[0]
  const toDay =
    frequency === Frequency.Weekly
      ? rangeEnds?.to ?? sortedDays[sortedDays.length - 1]
      : fromDay

  const startDate = dateForWeekdayThisWeek(fromDay)
  let endDate = dateForWeekdayThisWeek(toDay)

  if (
    frequency === Frequency.Weekly &&
    rangeEnds &&
    weekdayRangeWrapsAround(fromDay, toDay)
  ) {
    endDate = addDays(startDate, daysBetweenWeekdays(fromDay, toDay))
  }

  return {
    startDate,
    endDate,
  }
}

export const isDateAtLocalMidnight = (date: Date): boolean =>
  date.getHours() === 0 &&
  date.getMinutes() === 0 &&
  date.getSeconds() === 0 &&
  date.getMilliseconds() === 0

/**
 * Kody dni między dwiema datami (włącznie).
 * Koniec o północy jest traktowany jak koniec poprzedniego dnia.
 */
export const weekdaysBetweenDates = (
  startDate: Date | null,
  endDate: Date | null
) => {
  if (!startDate || !endDate) return []

  const endAtMidnight = isDateAtLocalMidnight(endDate)
  const midnightEndIsExclusive =
    endAtMidnight && isDateAtLocalMidnight(startDate)

  const endDateForCount = midnightEndIsExclusive
    ? addDays(endDate, -1)
    : endDate

  const dayCount = Math.max(
    differenceInCalendarDays(endDateForCount, startDate),
    0
  )
  const startDayIndex = (startDate.getDay() + 6) % 7

  return Array.from({ length: dayCount + 1 }, (_, offset) => {
    const dayIndex = (startDayIndex + offset) % WEEK_DAYS.length

    return WEEK_DAYS[dayIndex]
  })
}

/**
 * Dni tygodnia z DTSTART/DTEND przy FREQ=WEEKLY bez BYDAY.
 * Gdy oba końce są tego samego dnia (np. śr 8:00 → śr+7d 6:00), to jeden dzień w cyklu,
 * a nie pełny zakres kalendarzowy między datami (który dałby śr→wt).
 */
export const weekdaysFromWeeklyRecurringRange = (
  startDate: Date | null,
  endDate: Date | null
): string[] => {
  if (!startDate || !endDate) return []

  const startDay = weekdayCodeFromDate(startDate)

  const endAtMidnight = isDateAtLocalMidnight(endDate)
  const midnightEndIsExclusive =
    endAtMidnight && isDateAtLocalMidnight(startDate)

  const endDayForComparison = midnightEndIsExclusive
    ? weekdayCodeFromDate(addDays(endDate, -1))
    : weekdayCodeFromDate(endDate)

  if (startDay === endDayForComparison) {
    return [startDay]
  }

  return weekdaysBetweenDates(startDate, endDate)
}

/** Czy oba końce mają godzinę 00:00 (cały dzień z iCal). */
export const isAllDaySchedule = (
  startDate: Date | null,
  endDate: Date | null
) => {
  if (!startDate || !endDate) return false

  return (
    startDate.getHours() === 0 &&
    startDate.getMinutes() === 0 &&
    endDate.getHours() === 0 &&
    endDate.getMinutes() === 0
  )
}

export function resolveScheduleAllDayFromIcal(
  startDate: Date | null,
  endDate: Date | null,
  frequency: Frequency
): boolean {
  if (frequency === Frequency.Daily) return false
  return isAllDaySchedule(startDate, endDate)
}

/**
 * Długość okna harmonogramu cyklicznego w ms.
 * Zawijanie, gdy koniec jest wcześniej na zegarze niż start:
 * - co tydzień: +7 dni (np. śr 18:00 → śr 06:00),
 * - codziennie / wybrane dni: +1 dzień (np. 22:00 → 06:00, śr 22:00 → czw 06:00 w iCal).
 */
export function getRecurringScheduleDurationMs(
  scheduleFromDate: Date,
  scheduleToDate: Date,
  frequency: Frequency,
  daysOfWeek: string[]
): number {
  let durationMs = scheduleToDate.getTime() - scheduleFromDate.getTime()

  if (durationMs <= 0) {
    if (frequency === Frequency.Weekly && daysOfWeek.length > 0) {
      durationMs =
        addDays(scheduleToDate, 7).getTime() - scheduleFromDate.getTime()
    } else if (
      frequency === Frequency.Daily ||
      frequency === Frequency.SelectedDays
    ) {
      durationMs =
        addDays(scheduleToDate, 1).getTime() - scheduleFromDate.getTime()
    }
  }

  return durationMs
}

/**
 * Godziny z iCal osadza na datach referencyjnych formularza.
 * Np. overnight: czw 6:00 → „do” na tym samym dniu co w UI (środa przy wybranej środzie).
 */
export function normalizeScheduleFormDates(
  scheduleFromDate: Date | null,
  scheduleToDate: Date | null,
  frequency: Frequency,
  daysOfWeek: string[]
): { scheduleFromDate: Date | null; scheduleToDate: Date | null } {
  if (!scheduleFromDate || !scheduleToDate) {
    return { scheduleFromDate, scheduleToDate }
  }

  const { startDate, endDate } = getReferenceScheduleDates({
    frequency,
    daysOfWeek,
  })

  return {
    scheduleFromDate: combineDateAndTime(startDate, scheduleFromDate),
    scheduleToDate: combineDateAndTime(endDate, scheduleToDate),
  }
}

/** Czy para dat/godzin ma dodatnią długość (z uwzględnieniem tygodnia z zawinięciem). */
export function isValidRecurringScheduleTimeRange(
  scheduleFromDate: Date | null | undefined,
  scheduleToDate: Date | null | undefined,
  frequency: Frequency,
  daysOfWeek: string[]
): boolean {
  if (!scheduleFromDate || !scheduleToDate) return true

  return (
    getRecurringScheduleDurationMs(
      scheduleFromDate,
      scheduleToDate,
      frequency,
      daysOfWeek
    ) > 0
  )
}

export function computeScheduleDisplayDurationMs(params: {
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  scheduleAllDay: boolean
  frequency: Frequency
  daysOfWeek: string[]
}): number | null {
  const {
    scheduleFromDate,
    scheduleToDate,
    scheduleAllDay,
    frequency,
    daysOfWeek,
  } = params

  if (scheduleAllDay) {
    if (frequency === Frequency.Daily) {
      return 24 * 60 * 60 * 1000
    }
    if (daysOfWeek.length === 0) return null
    const { startDate, endDate } = getReferenceScheduleDates({
      frequency,
      daysOfWeek,
    })
    const from = combineDateAndTime(startDate, null)
    const to = addDays(combineDateAndTime(endDate, null), 1)
    return to.getTime() - from.getTime()
  }

  if (!scheduleFromDate || !scheduleToDate) return null

  return getRecurringScheduleDurationMs(
    scheduleFromDate,
    scheduleToDate,
    frequency,
    daysOfWeek
  )
}
